function childByLocalName(parent, localName) {
  if (!parent?.children) {
    return null;
  }
  return [...parent.children].find((c) => c.localName === localName) || null;
}

function findFirstDescendantByLocalName(root, localName) {
  if (!root) {
    return null;
  }
  if (root.localName === localName) {
    return root;
  }
  for (const child of root.children || []) {
    const found = findFirstDescendantByLocalName(child, localName);
    if (found) {
      return found;
    }
  }
  return null;
}

/**
 * Read extent from a WMS &lt;Layer&gt; element (GetCapabilities XML), matching
 * EX_GeographicBoundingBox / BoundingBox / LatLonBoundingBox as returned e.g. by GeoServer.
 * @returns {[[number, number], [number, number]] | null} Leaflet [[south,west],[north,east]]
 */
function boundsFromLayerXml(layerEl) {
  const ex = childByLocalName(layerEl, "EX_GeographicBoundingBox");
  if (ex) {
    const num = (tag) =>
      parseFloat(childByLocalName(ex, tag)?.textContent?.trim() || "NaN");
    const w = num("westBoundLongitude");
    const e = num("eastBoundLongitude");
    const s = num("southBoundLatitude");
    const n = num("northBoundLatitude");
    if (![w, e, s, n].some(Number.isNaN)) {
      return [
        [s, w],
        [n, e],
      ];
    }
  }

  const ll = [...(layerEl.children || [])].find(
    (c) => c.localName === "LatLonBoundingBox"
  );
  if (ll) {
    const minx = parseFloat(ll.getAttribute("minx") || "");
    const miny = parseFloat(ll.getAttribute("miny") || "");
    const maxx = parseFloat(ll.getAttribute("maxx") || "");
    const maxy = parseFloat(ll.getAttribute("maxy") || "");
    if (![minx, miny, maxx, maxy].some(Number.isNaN)) {
      return [
        [miny, minx],
        [maxy, maxx],
      ];
    }
  }

  const boxes = [...(layerEl.children || [])].filter(
    (c) => c.localName === "BoundingBox"
  );
  for (const bb of boxes) {
    const crs = String(bb.getAttribute("CRS") || bb.getAttribute("crs") || "")
      .toUpperCase()
      .trim();
    const minx = parseFloat(bb.getAttribute("minx") || "");
    const miny = parseFloat(bb.getAttribute("miny") || "");
    const maxx = parseFloat(bb.getAttribute("maxx") || "");
    const maxy = parseFloat(bb.getAttribute("maxy") || "");
    if ([minx, miny, maxx, maxy].some(Number.isNaN)) {
      continue;
    }
    if (crs === "CRS:84") {
      return [
        [miny, minx],
        [maxy, maxx],
      ];
    }
    if (crs === "EPSG:4326") {
      const latish = Math.abs(minx) <= 90 && Math.abs(maxx) <= 90;
      const lonish = Math.abs(miny) <= 180 && Math.abs(maxy) <= 180;
      if (latish && lonish) {
        return [
          [minx, miny],
          [maxx, maxy],
        ];
      }
      return [
        [miny, minx],
        [maxy, maxx],
      ];
    }
  }

  return null;
}

/**
 * Parse WMS GetCapabilities XML in the browser (DOMParser).
 * Prefer this over JSON from wms-capabilities for correct EX_GeographicBoundingBox handling.
 * @returns {Array<{ name: string, title: string, bounds: [[number,number],[number,number]] | null }>}
 */
export function wmsCapabilityLayersFromXml(xmlString) {
  if (typeof DOMParser === "undefined" || !xmlString) {
    return [];
  }
  const doc = new DOMParser().parseFromString(xmlString, "application/xml");
  const err = doc.querySelector("parsererror");
  if (err) {
    return [];
  }
  const root = doc.documentElement;
  if (!root) {
    return [];
  }
  const capability = findFirstDescendantByLocalName(root, "Capability");
  if (!capability) {
    return [];
  }
  const topLayer = childByLocalName(capability, "Layer");
  if (!topLayer) {
    return [];
  }

  const out = [];
  function walk(layerEl) {
    const subLayers = [...(layerEl.children || [])].filter(
      (c) => c.localName === "Layer"
    );
    subLayers.forEach(walk);

    const nameEl = childByLocalName(layerEl, "Name");
    const name = nameEl?.textContent?.trim();
    if (!name) {
      return;
    }
    const titleEl = childByLocalName(layerEl, "Title");
    out.push({
      name,
      title: (titleEl?.textContent || "").trim() || name,
      bounds: boundsFromLayerXml(layerEl),
    });
  }
  walk(topLayer);
  return out;
}

/**
 * Leaflet LatLngBoundsLiteral: [[south, west], [north, east]]
 */
export function wmsLayerLatLngBoundsFromCapability(layer) {
  if (!layer || typeof layer !== "object") {
    return null;
  }

  const ex = layer.EX_GeographicBoundingBox;
  if (ex) {
    const w = parseFloat(
      ex.westBoundLongitude ?? ex.westboundlongitude ?? ex.WestBoundLongitude
    );
    const e = parseFloat(
      ex.eastBoundLongitude ?? ex.eastboundlongitude ?? ex.EastBoundLongitude
    );
    const s = parseFloat(
      ex.southBoundLatitude ?? ex.southboundlatitude ?? ex.SouthBoundLatitude
    );
    const n = parseFloat(
      ex.northBoundLatitude ?? ex.northboundlatitude ?? ex.NorthBoundLatitude
    );
    if ([w, e, s, n].every((v) => !Number.isNaN(v))) {
      return [
        [s, w],
        [n, e],
      ];
    }
  }

  const latLon = layer.LatLonBoundingBox ?? layer.latLonBoundingBox;
  if (latLon) {
    const attrs = latLon.$ || latLon;
    const minx = parseFloat(attrs.minx ?? latLon.minx);
    const miny = parseFloat(attrs.miny ?? latLon.miny);
    const maxx = parseFloat(attrs.maxx ?? latLon.maxx);
    const maxy = parseFloat(attrs.maxy ?? latLon.maxy);
    if (![minx, miny, maxx, maxy].some((v) => Number.isNaN(v))) {
      return [
        [miny, minx],
        [maxy, maxx],
      ];
    }
  }

  const raw = layer.BoundingBox;
  const boxes = Array.isArray(raw) ? raw : raw ? [raw] : [];
  for (const bb of boxes) {
    const attrs = bb.$ || bb;
    const crs = String(attrs.crs ?? attrs.CRS ?? "").toUpperCase();
    if (crs !== "CRS:84" && crs !== "EPSG:4326") {
      continue;
    }
    const minx = parseFloat(attrs.minx ?? bb.minx);
    const miny = parseFloat(attrs.miny ?? bb.miny);
    const maxx = parseFloat(attrs.maxx ?? bb.maxx);
    const maxy = parseFloat(attrs.maxy ?? bb.maxy);
    if ([minx, miny, maxx, maxy].some((v) => Number.isNaN(v))) {
      continue;
    }
    if (crs === "CRS:84") {
      return [
        [miny, minx],
        [maxy, maxx],
      ];
    }
    // EPSG:4326 from GeoServer: often minx=lat, miny=lon in capabilities
    const latish = Math.abs(minx) <= 90 && Math.abs(maxx) <= 90;
    const lonish = Math.abs(miny) <= 180 && Math.abs(maxy) <= 180;
    if (latish && lonish) {
      return [
        [minx, miny],
        [maxx, maxy],
      ];
    }
    return [
      [miny, minx],
      [maxy, maxx],
    ];
  }

  return null;
}

/** @param rootLayer result.Capability.Layer from wms-capabilities parse */
export function wmsCapabilityLayersToEntries(rootLayer) {
  if (!rootLayer) {
    return [];
  }
  const multiLayer = rootLayer.Layer;
  if (!multiLayer && rootLayer.Name) {
    return [
      {
        name: rootLayer.Name,
        title: rootLayer.Title,
        bounds: wmsLayerLatLngBoundsFromCapability(rootLayer),
      },
    ];
  }
  if (!multiLayer) {
    return [];
  }
  const top = Array.isArray(multiLayer) ? multiLayer : [multiLayer];
  let flat;
  if (top.length === 1 && top[0].Layer) {
    const nested = top[0].Layer;
    flat = Array.isArray(nested) ? nested : [nested];
  } else {
    flat = top;
  }
  return flat.map((x) => ({
    name: x.Name,
    title: x.Title,
    bounds: wmsLayerLatLngBoundsFromCapability(x),
  }));
}
